import { FormShape, FormUnit, type GetFormInput, type GetFormOutputVerbose } from "@suse-o11y/signal/form";
import { z } from "zod";

import { isStringDirty } from "~/tools/is-string-dirty";
import { Template } from "~/tools/template";

import { DEFAULT, type Spec } from "./spec";

type PromqlFormState = FormUnit<string, ReadonlyArray<string>, Template<["${", "}"], null>>;
const promqlSchema = z
	.string()
	.trim()
	.min(1, "PromQL cannot be empty")
	.transform((promql) => Template<null>(promql));

type AliasFormState = FormUnit<string, null, undefined | Template<["${", "}"], null>>;

type MinStepFormState = FormUnit<string, null, undefined | Template<["${", "}"], null>>;

type FormState = FormShape<{
	promql: PromqlFormState;
	alias: AliasFormState;
	minStep: MinStepFormState;
}>;

type Input = GetFormInput<FormState>;

const INITIAL: Input = {
	promql: DEFAULT.promql.source,
	alias: DEFAULT.alias.source,
	minStep: DEFAULT.minStep.source,
};

function fromSpec(spec: undefined | Spec): Input {
	return {
		promql: spec?.promql.source ?? INITIAL.promql,
		alias: spec?.alias?.source ?? INITIAL.alias,
		minStep: spec?.minStep?.source ?? INITIAL.minStep,
	};
}

function init(initial?: Input): FormState {
	return FormShape({
		promql: FormUnit(initial?.promql ?? INITIAL.promql, {
			schema: promqlSchema,
			isInputDirty: isStringDirty,
		}),

		alias: FormUnit(initial?.alias ?? INITIAL.alias, {
			transform: (input) => {
				const trimmed = input.trim();

				return trimmed ? Template(trimmed) : undefined;
			},
			isInputDirty: isStringDirty,
		}),

		minStep: FormUnit(initial?.minStep ?? INITIAL.minStep, {
			isInputDirty: isStringDirty,
			transform: (input) => {
				const trimmed = input.trim();

				return trimmed ? Template(trimmed) : undefined;
			},
		}),
	});
}

function toSpec(output: GetFormOutputVerbose<FormState>): Spec {
	return {
		promql: output.promql ?? DEFAULT.promql,
		alias: output.alias ?? undefined,
		minStep: output.minStep ?? undefined,
	};
}

const FormState = {
	initial: INITIAL,
	init,
	fromSpec,
	toSpec,
} as const;

export { FormState };
export type { PromqlFormState, AliasFormState, MinStepFormState };
